import React, { useState, useContext, useEffect } from "react";
import "../../App.css"; // Import CSS file for styling
import { UserContext } from "../context/UserContext"; // Import UserContext
import axios from "axios";

const UserProfile = ({ onClose }) => {
  // Fetch user data from UserContext
  const { 
    firstName: userFirstName, 
    lastName: userLastName, 
    emailId, 
    postcode: userPostcode,
    address: userAddress,
    state: userState,
    electoralDivision: userElectoralDivision,
    suburb: userSuburb,
    setFirstName: setContextFirstName,
    setLastName: setContextLastName,
    setPostcode: setContextPostcode,
    setAddress: setContextAddress,
    setState: setContextState,
    setElectoralDivision: setContextElectoralDivision,
    setSuburb: setContextSuburb
  } = useContext(UserContext);

  // State to hold form data
  const [formData, setFormData] = useState({
    firstName: userFirstName || "",
    lastName: userLastName || "",
    postcode: userPostcode || "",
    address: userAddress || "",
    state: userState || "",
    electoralDivision: userElectoralDivision || "",
    suburb: userSuburb || ""
  });

  // State to track if fields are editable
  const [isEditing, setIsEditing] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Handle input changes
  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [id]: value,
    }));

    // If address is being typed and editing is enabled, fetch suggestions
    if (id === "address" && isEditing && value.length > 2) {
      fetchAddressSuggestions(value);
    }
  };

  const fetchAddressSuggestions = async (input) => {
    setLoading(true);
    try {
      const response = await axios.post(
        "/api/address/autocomplete",
        {
          streetAddress: input,
        }
      );
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error("Error fetching address suggestions:", error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSuggestion = async (suggestion) => {
    try {
      // Get LGA and electorate information
      const response = await axios.post(
        "/api/address/info",
        {
          streetAddress: suggestion.streetAddress,
          suburb: suggestion.suburb,
          state: suggestion.state,
          postCode: suggestion.postCode
        }
      );
      
      const stateMapping = {
        VIC: "Victoria",
        NSW: "New South Wales",
        QLD: "Queensland",
        SA: "South Australia",
        WA: "Western Australia",
        TAS: "Tasmania",
        NT: "Northern Territory",
        ACT: "Australian Capital Territory",
      };

      setFormData(prev => ({
        ...prev,
        address: suggestion.streetAddress,
        suburb: suggestion.suburb,
        postcode: suggestion.postCode,
        state: stateMapping[suggestion.state] || suggestion.state,
        electoralDivision: response.data.electoralDivision || prev.electoralDivision
      }));

      setSuggestions([]); // Clear suggestions after selection
    } catch (error) {
      console.error("Error fetching address information:", error);
    }
  };

  // Handle form submission
  const handleUpdate = async () => {
    try {
      const response = await fetch("/api/update-profile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          emailId,
          ...formData
        }),
      });

      if (response.ok) {
        // Update context with new values
        setContextFirstName(formData.firstName);
        setContextLastName(formData.lastName);
        setContextPostcode(formData.postcode);
        setContextAddress(formData.address);
        setContextState(formData.state);
        setContextElectoralDivision(formData.electoralDivision);
        setContextSuburb(formData.suburb);

        alert("Profile updated successfully!");
        setIsEditing(false);
      } else {
        alert("Failed to update profile");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      alert("Error updating profile");
    }
  };

  return (
    <div
      className="user-profile-overlay"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.5)", // Dark semi-transparent background
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
      }}
    >
      <div
        className="user-profile-popup"
        style={{
          backgroundColor: "white",
          borderRadius: "10px",
          padding: "20px",
          width: "90%",
          maxWidth: "600px", // Increased width for better alignment
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
          position: "relative",
          textAlign: "center",
        }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            background: "transparent",
            border: "none",
            fontSize: "18px",
            cursor: "pointer",
            fontWeight: "bold",
          }}
        >
          ✕
        </button>
        <h2 style={{ marginBottom: "20px" }}>USER PROFILE</h2>
        
        {/* Profile Header - Modified for side-by-side layout */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '20px',
          gap: '15px' // Space between circle and text
        }}>
          {/* Circle with First Letter */}
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: '#e0e0e0',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: '30px',
            fontWeight: 'bold',
            color: '#666'
          }}>
            {userFirstName ? userFirstName.charAt(0).toUpperCase() : 'A'}
          </div>

          {/* User Details */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center'
          }}>
            <p style={{
              margin: '0',
              fontWeight: 'bold',
              fontSize: '20px'
            }}>
              {userFirstName} {userLastName}
            </p>
            <p style={{
              margin: '5px 0 0 0',
              color: '#666',
              fontSize: '16px',
              fontWeight: 'bold'
            }}>
              ({emailId})
            </p>
          </div>
        </div>

        {/* Profile Fields with Flex Layout */}
        {["firstName", "lastName", "address", "suburb", "postcode", "state", "electoralDivision"].map((field) => (
          <div key={field} className="form-field" style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "10px",
            position: "relative" // Added for suggestions dropdown
          }}>
            <label htmlFor={field} style={{
              fontWeight: "bold",
              flex: "1",
              textAlign: "right",
              marginRight: "10px",
            }}>
              {field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1')}:
            </label>
            <div style={{ flex: "2", position: "relative" }}>
              <input
                type="text"
                id={field}
                value={formData[field]}
                onChange={handleChange}
                disabled={!isEditing || field === "state" || field === "electoralDivision" || field === "postcode" || field === "suburb"}
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "5px",
                  border: "1px solid #ccc",
                  backgroundColor: (!isEditing || field === "state" || field === "electoralDivision" || field === "postcode" || field === "suburb") ? "#f5f5f5" : "white",
                }}
              />
              
              {/* Address Suggestions Dropdown */}
              {field === "address" && isEditing && suggestions.length > 0 && (
                <ul style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  listStyle: "none",
                  margin: 0,
                  padding: 0,
                  maxHeight: "150px",
                  overflowY: "auto",
                  border: "1px solid #ddd",
                  borderRadius: "5px",
                  backgroundColor: "white",
                  zIndex: 1000,
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                }}>
                  {suggestions.map((suggestion, index) => (
                    <li
                      key={index}
                      onClick={() => handleSelectSuggestion(suggestion)}
                      style={{
                        padding: "8px 12px",
                        cursor: "pointer",
                        borderBottom: "1px solid #eee",
                        hover: {
                          backgroundColor: "#f5f5f5"
                        }
                      }}
                    >
                      {`${suggestion.streetAddress}, ${suggestion.suburb}, ${suggestion.state}, ${suggestion.postCode}`}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ))}

        {/* Action Buttons */}
        <div style={{ marginTop: "20px" }}>
          {isEditing ? (
            <button
              onClick={handleUpdate}
              style={{
                padding: "10px 20px",
                backgroundColor: "#28a745",
                color: "white",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
                marginRight: "10px",
              }}
            >
              Update
            </button>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              style={{
                padding: "10px 20px",
                backgroundColor: "#007bff",
                color: "white",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
              }}
            >
              Edit Profile
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
